import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import createJuggleVisionTheme from './theme';
import { db, auth } from './firebase/init';
import { FirebaseProvider } from './contexts/FirebaseContext';
import { RoleProvider } from './contexts/RoleContext';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeModeContext';
import AuthorizedApp from './components/AuthorizedApp';
import LoadingScreen from './components/LoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import logger from './utils/logger';

interface AppContentProps {
  user: any;
  authLoading: boolean;
}

const AppContent: React.FC<AppContentProps> = ({ user, authLoading }) => {
  const { mode } = useThemeMode();
  const theme = useMemo(() => createJuggleVisionTheme(mode), [mode]);

  if (authLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <LoadingScreen />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      <FirebaseProvider db={db} auth={auth} user={user} loading={authLoading}>
        <RoleProvider db={db} user={user}>
          <AuthProvider>
            <Routes>
              <Route
                path="/admin/*"
                element={
                  <ErrorBoundary>
                    <React.Suspense fallback={<LoadingScreen />}>
                      <AuthorizedApp />
                    </React.Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/stats/*"
                element={
                  <ErrorBoundary>
                    <React.Suspense fallback={<LoadingScreen />}>
                      <AuthorizedApp />
                    </React.Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/billing/*"
                element={
                  <ErrorBoundary>
                    <React.Suspense fallback={<LoadingScreen />}>
                      <AuthorizedApp />
                    </React.Suspense>
                  </ErrorBoundary>
                }
              />
              <Route
                path="/*"
                element={
                  <ErrorBoundary>
                    <React.Suspense fallback={<LoadingScreen />}>
                      <AuthorizedApp />
                    </React.Suspense>
                  </ErrorBoundary>
                }
              />
            </Routes>
          </AuthProvider>
        </RoleProvider>
      </FirebaseProvider>
    </ThemeProvider>
  );
};

function App() {
  const [user, setUser] = useState<any>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authInitialized, setAuthInitialized] = useState(false);

  useEffect(() => {
    let authTimeout: NodeJS.Timeout;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      logger.log('🔍 Main App: onAuthStateChanged fired, user:', firebaseUser?.email || 'null');
      setUser(firebaseUser);
      setAuthInitialized(true);

      if (authTimeout) clearTimeout(authTimeout);

      if (!firebaseUser) {
        logger.log('🔍 Main App: No user detected, waiting 1 second for auth to initialize');
        authTimeout = setTimeout(() => {
          logger.log('🔍 Main App: Auth timeout reached, proceeding without user');
          setAuthLoading(false);
        }, 1000);
      } else {
        setAuthLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (authTimeout) clearTimeout(authTimeout);
    };
  }, [authInitialized]);

  return (
    <BrowserRouter>
      <ThemeModeProvider>
        <AppContent user={user} authLoading={authLoading} />
      </ThemeModeProvider>
    </BrowserRouter>
  );
}

export default App;
